import { useEffect, useState } from 'react';
import { salApi, commesseConsulentiApi, apriProtetto } from '../api';
import CampoImporto from './CampoImporto';
import AutorizzazioneExtra from './AutorizzazioneExtra';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const oggi = () => new Date().toISOString().slice(0, 10);

const TIPI = [
  { v: 'front_office', l: 'Front office', hint: 'presso il cliente' },
  { v: 'back_office', l: 'Back office', hint: 'da remoto / documentale' },
];
const UNITA = [
  { v: 'giorno', l: 'Giornata intera' },
  { v: 'mezza_giornata', l: 'Mezza giornata' },
  { v: 'ora', l: 'Ore' },
  { v: 'intervento', l: 'Attività (a intervento)' },
];
const CATEGORIE = [
  { v: 'km', l: 'Chilometri', chiedeKm: true },
  { v: 'telepass', l: 'Telepass / pedaggi' },
  { v: 'vitto', l: 'Vitto' },
  { v: 'alloggio', l: 'Alloggio' },
  { v: 'parking', l: 'Parcheggio' },
  { v: 'altro', l: 'Altro' },
];

const etichettaUnita = u => UNITA.find(x => x.v === u)?.l || u;
const etichettaTipo = t => TIPI.find(x => x.v === t)?.l || t;
const etichettaCat = c => CATEGORIE.find(x => x.v === c)?.l || c;
const qta = (n) => {
  const v = Number(n) || 0;
  return String(Number.isInteger(v) ? v : +v.toFixed(2)).replace('.', ',');
};
const ETICHETTA_UNITA = { giorno: 'giornate', mezza_giornata: 'mezze giornate', ora: 'ore',
                          intervento: 'attività' };

// Stessa convenzione e stessa regola del server (lib/tariffe.js): giornata = 8
// ore, mezza = 4, e sotto l'unità piena per meno di un'ora si conta l'unità
// intera. Serve solo per l'anteprima: il valore che fa fede lo calcola il server.
const SOGLIA_ORE = 1;
const proponiQuantita = (ore, unita) => {
  const h = Number(ore) || 0;
  // A intervento quel numero sono già le attività svolte, non delle ore.
  if (unita === 'intervento') return h > 0 ? h : null;
  if (h <= 0) return null;
  if (unita === 'ora') return h;
  const pieno = unita === 'giorno' ? 8 : 4;
  const intere = Math.floor(h / pieno);
  const resto = +(h - intere * pieno).toFixed(6);
  if (resto === 0) return intere;
  if (resto >= pieno - SOGLIA_ORE) return intere + 1;
  return +(intere + resto / pieno).toFixed(4);
};

// Come lo legge il CLIENTE: in percentuale dell'unità, mai in ore.
const etichettaQuantita = (q, unita) => {
  if (unita === 'intervento') {
    const n = Number(q) || 0;
    return `${String(n).replace('.', ',')} ${n === 1 ? 'attività' : 'attività'}`;
  }
  if (q === null || q === undefined) return '';
  const n = Number(q);
  if (unita === 'ora') return `${String(n).replace('.', ',')} ore`;
  const uno = unita === 'giorno' ? 'giornata' : 'mezza giornata';
  const molti = unita === 'giorno' ? 'giornate' : 'mezze giornate';
  if (Number.isInteger(n)) return `${n} ${n === 1 ? uno : molti}`;
  const intere = Math.floor(n);
  const perc = Math.round((n - intere) * 100);
  return intere ? `${intere} ${intere === 1 ? uno : molti} + ${perc}%` : `${perc}% di ${uno}`;
};

/**
 * Scheda di rendicontazione di un SAL.
 * `soloLettura` per l'admin in fase di approvazione; `mostraRicavo` solo a noi.
 */
export default function SchedaSal({ salId, commessa, soloLettura = false, mostraRicavo = false, onCambiato }) {
  const [sal, setSal] = useState(null);
  const [caricando, setCaricando] = useState(true);
  const [errore, setErrore] = useState('');

  const economia = (sal?.modalita_esecuzione || commessa?.modalita_esecuzione) === 'economia';
  // Il "compenso" ha senso solo per un consulente ESTERNO: quando rendiconta
  // qualcuno di Sinthema il costo interno non si misura qui, serve solo sapere
  // quanto fatturare al cliente.
  const compensoPertinente = (sal?.utente_ruolo || 'esterno') === 'esterno';

  const [att, setAtt] = useState({ data: oggi(), tipo: 'front_office', unita: 'giorno',
                                   quantita: '1', descrizione: '', tariffa: '', tariffa_motivo: '' });
  // Le spese restano facoltative, ma non devono essere introvabili: in economia
  // (trasferte, km, vitto) il pannello parte aperto; a corpo parte chiuso, con
  // un pulsante evidente. Chiuderlo del tutto lo aveva reso invisibile.
  const [mostraSpese, setMostraSpese] = useState(null);
  const [spesa, setSpesa] = useState({ data: oggi(), categoria: 'km', descrizione: '', km: '',
                                       importo: '', ribaltabile: true, consulente_id: '', tariffa_km: '' });
  const [file, setFile] = useState(null);
  const [salvando, setSalvando] = useState(false);
  // Testata modificabile: descrizione, data e — per le commesse a corpo —
  // l'avanzamento. Prima si potevano indicare solo alla creazione, e un
  // rendiconto lasciato a metà restava "(senza descrizione)" per sempre.
  const [testata, setTestata] = useState(null);
  const [salvataOra, setSalvataOra] = useState(false);

  // Listino della commessa: se esiste, la rendicontazione avviene per voce + ore
  const [voci, setVoci] = useState([]);
  const [rigaVoce, setRigaVoce] = useState({ data: oggi(), voce_id: '', ore: '', descrizione: '',
                                             consulente_id: '', tariffa: '', tariffa_motivo: '' });
  // Consulenti assegnati alla commessa: chi compila può attribuire le righe
  // a ciascuno di loro. Un esterno non sceglie: le righe sono sempre sue.
  const [consulentiCommessa, setConsulentiCommessa] = useState([]);

  const carica = () => salApi.get(salId)
    .then(d => {
      setSal(d);
      // La testata si riallinea al dato salvato solo se non la si sta già
      // modificando: ricaricare dopo aver aggiunto una riga non deve
      // cancellare una descrizione appena scritta e non ancora salvata.
      setTestata(t => t ?? {
        descrizione: d.descrizione || '',
        data_sal: (d.data_sal || '').slice(0, 10),
        percentuale: d.percentuale ?? '',
        // A corpo si dichiara in percentuale o come cifra: il rendiconto si
        // riapre nel modo in cui era stato compilato.
        modo: d.compenso_dichiarato != null ? 'importo' : 'percentuale',
        compenso: d.compenso_dichiarato ?? '',
      });
    })
    .catch(e => setErrore(String(e))).finally(() => setCaricando(false));

  const salvaBozza = async () => {
    setSalvando(true);
    try {
      const avanzamento = testata.modo === 'importo'
        ? { compenso_dichiarato: testata.compenso === '' ? null : Number(testata.compenso) }
        : { percentuale: testata.percentuale === '' ? null : Number(testata.percentuale) };
      await salApi.update(salId, {
        descrizione: testata.descrizione,
        data_sal: testata.data_sal || null,
        ...(economia ? {} : avanzamento),
      });
      setSalvataOra(true);
      setTimeout(() => setSalvataOra(false), 2500);
      await carica();
      onCambiato?.();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };
  useEffect(() => { setCaricando(true); carica(); }, [salId]);

  const commessaId = sal?.commessa_id || commessa?.id;
  // Le tariffe concordate servono anche sulle commesse a corpo: lì il prezzo
  // fisso copre il lavoro previsto, ma le attività aggiuntive si valorizzano
  // proprio con le tariffe scritte in offerta, invece che a mano.
  useEffect(() => {
    if (!commessaId) return;
    salApi.vociCommessa(commessaId)
      .then(v => { setVoci(v); if (v.length) setRigaVoce(r => ({ ...r, voce_id: r.voce_id || String(v[0].id) })); })
      .catch(() => setVoci([]));
  }, [commessaId]);

  useEffect(() => {
    if (!commessaId || soloLettura) return;
    commesseConsulentiApi.list(commessaId).then(setConsulentiCommessa).catch(() => setConsulentiCommessa([]));
  }, [commessaId, soloLettura]);

  // La tariffa al chilometro è quella concordata con il consulente e arriva
  // con la commessa; quella scritta sulla riga vince su tutte.
  const kmPrevista = Number(commessa?.mie_tariffe?.km ?? sal?.tariffa_km ?? commessa?.tariffa_km ?? 0.45);
  // Quanto si riaddebita al cliente: la tariffa della commessa, o la clausola
  // standard delle nostre offerte quando la commessa non ne porta una. È un
  // numero diverso dal rimborso, e vederli vicini evita di confonderli.
  const kmCliente = commessa?.tariffa_km_cliente === null || commessa?.tariffa_km_cliente === undefined
    ? 0.65 : Number(commessa.tariffa_km_cliente);
  const tariffaKm = spesa.tariffa_km === '' || spesa.tariffa_km === undefined
    ? kmPrevista : Number(spesa.tariffa_km);
  // Il rimborso km è proposto dal sistema ma resta correggibile
  const importoProposto = spesa.categoria === 'km' && spesa.km
    ? +(tariffaKm * Number(spesa.km)).toFixed(2) : null;

  const aggiungiAttivita = async () => {
    if (!att.quantita || Number(att.quantita) <= 0) return alert('Indica la quantità');
    setSalvando(true);
    try {
      // Su una commessa a corpo tutto ciò che si rendiconta a ore è, per
      // definizione, lavoro che a contratto non c'era.
      await salApi.addAttivita(salId, {
        ...att, extra_contratto: economia ? !!att.extra_contratto : true,
      });
      setAtt(a => ({ ...a, quantita: att.unita === 'ora' ? '' : '1', descrizione: '' }));
      await carica(); onCambiato?.();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const aggiungiAttivitaVoce = async () => {
    if (!rigaVoce.voce_id) return alert('Scegli l\'attività dal listino');
    if (!rigaVoce.ore || Number(rigaVoce.ore) <= 0) return alert('Indica le ore lavorate (anche frazioni, es. 3,5)');
    setSalvando(true);
    try {
      await salApi.addAttivita(salId, {
        data: rigaVoce.data, voce_id: Number(rigaVoce.voce_id),
        ore: Number(rigaVoce.ore), descrizione: rigaVoce.descrizione || undefined,
        consulente_id: rigaVoce.consulente_id || undefined,
        tariffa: rigaVoce.tariffa === '' ? undefined : rigaVoce.tariffa,
        tariffa_motivo: rigaVoce.tariffa_motivo || undefined,
        // Su una commessa a corpo ciò che si rendiconta a ore è per
        // definizione lavoro che a contratto non c'era.
        extra_contratto: economia ? !!rigaVoce.extra_contratto : true,
      });
      // La tariffa resta: su un progetto con condizioni diverse vale per tutte
      // le righe che si stanno inserendo.
      setRigaVoce(r => ({ ...r, ore: '', descrizione: '' }));
      await carica(); onCambiato?.();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  // Correzione della quantità fatturabile: prerogativa dell'amministratore.
  // Il server ferma una quantità che le ore non giustificano — digitare "5"
  // su una voce a giornata vale cinque giornate, non cinque ore — perché
  // fatturare più di quanto si è lavorato è una decisione, non un refuso.
  const correggiQuantita = async (rigaId, q, forza) => {
    try {
      await salApi.correggiQuantita(salId, rigaId, Number(q), forza);
      await carica(); onCambiato?.();
    } catch (e) {
      if (e?.conferma === 'quantita_oltre_le_ore'
          && confirm(`${e.error}\n\nFatturare comunque ${String(q).replace('.', ',')}?`)) {
        return correggiQuantita(rigaId, q, true);
      }
      alert(e?.error || e);
      await carica();   // rimette nel campo la quantità che c'era
    }
  };

  // Una riga passa dal consuntivo al cliente alla sola traccia interna e
  // viceversa: a corpo le ore non si fatturano, ma restano scritte.
  const cambiaAlCliente = async (rigaId, al_cliente) => {
    try { await salApi.updAttivita(salId, rigaId, { al_cliente }); await carica(); onCambiato?.(); }
    catch (e) { alert(e); }
  };

  // ── Correzione di una riga già inserita ──────────────────────────────────
  // Finché il rendiconto è aperto, una riga sbagliata si corregge invece di
  // essere eliminata e riscritta: chi rendiconta si accorge di un refuso dopo
  // aver salvato, ed è un gesto che capita di continuo.
  const [modifica, setModifica] = useState(null);   // { kind:'att'|'spesa', id, campi }
  const [fileModifica, setFileModifica] = useState(null);

  // ── Accorpamento a corpo di attività scelte ──────────────────────────────
  // Il cliente paga a forfait un gruppo di attività e a timesheet le altre.
  // Si spuntano le righe e si dà un nome e un importo: le righe restano
  // (documentano il lavoro e pagano il consulente) ma si fatturano col gruppo.
  const [selezione, setSelezione] = useState([]);
  const [nuovoCorpo, setNuovoCorpo] = useState({ descrizione: '', importo: '' });
  const [modCorpo, setModCorpo] = useState(null);   // { id, descrizione, importo }
  const seleziona = (id) => setSelezione(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const dopoCorpo = async () => { await carica(); onCambiato?.(); };

  const creaCorpo = async () => {
    setSalvando(true);
    try {
      await salApi.creaAccorpamento(salId, { ...nuovoCorpo, righe: selezione });
      setSelezione([]); setNuovoCorpo({ descrizione: '', importo: '' });
      await dopoCorpo();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };
  const salvaCorpo = async () => {
    setSalvando(true);
    try {
      await salApi.modificaAccorpamento(salId, modCorpo.id,
        { descrizione: modCorpo.descrizione, importo: modCorpo.importo });
      setModCorpo(null);
      await dopoCorpo();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };
  const aggiungiAlCorpo = async (g) => {
    const gia = (sal.attivita || []).filter(a => a.accorpamento_id === g.id).map(a => a.id);
    try {
      await salApi.modificaAccorpamento(salId, g.id, { righe: [...gia, ...selezione] });
      setSelezione([]);
      await dopoCorpo();
    } catch (e) { alert(e); }
  };
  const togliDalCorpo = async (a) => {
    const resto = (sal.attivita || []).filter(x => x.accorpamento_id === a.accorpamento_id && x.id !== a.id).map(x => x.id);
    if (!resto.length) return alert('È l\'ultima attività dell\'accorpamento: per toglierla scioglilo.');
    try { await salApi.modificaAccorpamento(salId, a.accorpamento_id, { righe: resto }); await dopoCorpo(); }
    catch (e) { alert(e); }
  };
  const sciogliCorpo = async (g) => {
    if (!confirm(`Sciogliere "${g.descrizione}"?\n\nLe attività tornano a timesheet e si fattureranno una per una.`)) return;
    try { await salApi.sciogliAccorpamento(salId, g.id); await dopoCorpo(); }
    catch (e) { alert(e); }
  };
  const campo = (k, v) => setModifica(m => ({ ...m, campi: { ...m.campi, [k]: v } }));

  const apriAtt = (a) => setModifica({ kind: 'att', id: a.id, campi: {
    data: (a.data || '').slice(0, 10),
    voce_id: a.voce_id ? String(a.voce_id) : '',
    ore: a.ore ?? '', tipo: a.tipo, unita: a.unita, quantita: a.quantita,
    descrizione: a.descrizione || '',
    consulente_id: a.consulente_id ? String(a.consulente_id) : '',
    // Il campo resta vuoto se la tariffa era quella prevista: vuoto significa
    // "vale quella concordata", non "zero".
    tariffa: a.tariffa_applicata != null && Number(a.tariffa_applicata) !== Number(a.tariffa_prevista)
      ? a.tariffa_applicata : '',
    tariffa_motivo: a.tariffa_motivo || '',
    extra_contratto: !!a.extra_contratto,
    fuori_budget: !!a.fuori_budget,
    ricavo: a.ricavo ?? '',
  } });

  const apriSpesa = (s) => { setFileModifica(null); setModifica({ kind: 'spesa', id: s.id, campi: {
    data: (s.data || '').slice(0, 10), categoria: s.categoria,
    km: s.km ?? '', importo: s.importo, descrizione: s.descrizione || '',
    ribaltabile: !!s.ribaltabile,
    consulente_id: s.consulente_id ? String(s.consulente_id) : '',
    tariffa_km: s.tariffa_km ?? '',
  } }); };

  const salvaModifica = async () => {
    const c = modifica.campi;
    setSalvando(true);
    try {
      if (modifica.kind === 'att') {
        // Il percorso resta quello della riga: a listino si correggono voce e
        // ore, senza listino tipo, unità e quantità.
        const dati = c.voce_id
          ? { data: c.data, voce_id: Number(c.voce_id), ore: Number(c.ore), descrizione: c.descrizione }
          : { data: c.data, tipo: c.tipo, unita: c.unita, quantita: Number(c.quantita), descrizione: c.descrizione };
        if (c.consulente_id) dati.consulente_id = Number(c.consulente_id);
        // Stringa vuota = "torna alla tariffa prevista"
        dati.tariffa = c.tariffa === '' ? '' : Number(c.tariffa);
        dati.tariffa_motivo = c.tariffa_motivo || '';
        dati.extra_contratto = !!c.extra_contratto;
        // L'importo al cliente lo decidiamo noi: il consulente non lo invia mai.
        if (mostraRicavo) dati.ricavo = c.ricavo === '' ? '' : Number(c.ricavo);
        await salApi.updAttivita(salId, modifica.id, dati);
      } else {
        await salApi.updSpesa(salId, modifica.id, {
          data: c.data, categoria: c.categoria,
          km: c.categoria === 'km' ? (c.km === '' ? '' : c.km) : '',
          importo: c.importo, descrizione: c.descrizione,
          ribaltabile: String(!!c.ribaltabile),
          consulente_id: c.consulente_id || '',
          tariffa_km: c.categoria === 'km' ? (c.tariffa_km === '' ? '' : c.tariffa_km) : '',
        }, fileModifica);
      }
      setModifica(null); setFileModifica(null);
      await carica(); onCambiato?.();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const aggiungiSpesa = async () => {
    const imp = spesa.importo !== '' ? spesa.importo : importoProposto;
    if (imp === null || imp === undefined || imp === '') return alert('Indica l\'importo');
    setSalvando(true);
    try {
      await salApi.addSpesa(salId, {
        ...spesa, importo: imp,
        ribaltabile: String(spesa.ribaltabile),
        consulente_id: spesa.consulente_id || '',
        tariffa_km: spesa.categoria === 'km' ? (spesa.tariffa_km === '' ? '' : spesa.tariffa_km) : '',
      }, file);
      // La tariffa al chilometro resta: vale per tutte le trasferte che si
      // stanno registrando.
      setSpesa(p => ({ data: oggi(), categoria: 'km', descrizione: '', km: '', importo: '',
                       ribaltabile: true, consulente_id: p.consulente_id, tariffa_km: p.tariffa_km }));
      setFile(null);
      await carica(); onCambiato?.();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const elimina = async (tipo, rigaId) => {
    if (!confirm('Eliminare questa riga?')) return;
    try {
      if (tipo === 'att') await salApi.delAttivita(salId, rigaId);
      else await salApi.delSpesa(salId, rigaId);
      await carica(); onCambiato?.();
    } catch (e) { alert(e); }
  };

  // Le righe si salvano una per una appena aggiunte: non c'è nulla in sospeso.
  // Quello che manca è il gesto esplicito di CHIUDERE il rendiconto e mandarlo
  // in approvazione — senza, non si capisce quando si è finito.
  const inviaInApprovazione = async () => {
    if (!confirm('Inviare il rendiconto in approvazione?\n\nDopo l\'invio non potrai più modificarlo, ma potrà essere rifiutato e riaperto.')) return;
    try { await salApi.invia(salId); await carica(); onCambiato?.(); }
    catch (e) { alert(e); }
  };

  const cambiaRibalto = async (rigaId, v) => {
    try { await salApi.ribaltaSpesa(salId, rigaId, v); await carica(); onCambiato?.(); }
    catch (e) { alert(e); }
  };

  if (caricando) return <div className="text-sm text-gray-400 py-6 text-center">Caricamento…</div>;
  if (errore) return <div className="text-sm text-red-600 py-4">{errore}</div>;
  if (!sal) return null;

  const attivita = sal.attivita || [];
  const spese = sal.spese || [];
  // null = non ancora deciso dall'utente: vale il default per modalità.
  const pannelloSpese = mostraSpese === null ? economia : mostraSpese;
  const totOre = attivita.filter(a => a.unita === 'ora').reduce((s, a) => s + Number(a.quantita), 0);
  const totGiorni = attivita.reduce((s, a) =>
    s + (a.unita === 'giorno' ? Number(a.quantita) : a.unita === 'mezza_giornata' ? Number(a.quantita) * 0.5 : 0), 0);
  // Come nella colonna: con l'unità "ore" le ore sono la quantità stessa.
  const totOreEffettive = attivita.reduce((s, a) =>
    s + (a.ore != null ? Number(a.ore) : a.unita === 'ora' ? Number(a.quantita) : 0), 0);
  const totCosto = attivita.reduce((s, a) => s + Number(a.costo || 0), 0);
  // Fatturabile, con le stesse regole del server: le righe accorpate valgono
  // per il loro gruppo, il lavoro fuori contratto solo se autorizzato.
  const accorpamenti = sal.accorpamenti || [];
  const extraOk = sal.extra_stato === 'approvato';
  // A corpo le ore del collaboratore sono traccia interna: documentano il
  // lavoro svolto e il suo compenso, ma il cliente paga il corrispettivo
  // concordato e nel consuntivo non compaiono.
  const vaAlCliente = (a) => a.al_cliente !== false;
  const nInterne = attivita.filter(a => !vaAlCliente(a)).length;
  const totRicavo = attivita
    .filter(a => vaAlCliente(a) && !a.accorpamento_id && (!a.extra_contratto || extraOk))
    .reduce((s, a) => s + Number(a.ricavo || 0), 0)
    + accorpamenti.reduce((s, g) => s + Number(g.importo || 0), 0);
  // Si possono accorpare solo righe previste a contratto che vanno al cliente,
  // e solo finché il rendiconto è aperto: è una scelta di prezzo verso di lui.
  const puoAccorpare = mostraRicavo && !soloLettura && sal.stato !== 'approvato';
  const accorpabile = (a) => puoAccorpare && vaAlCliente(a) && !a.extra_contratto && !a.accorpamento_id;
  // Colonne della tabella delle attività: la riga di modifica le occupa tutte.
  const nColonneAtt = 7 + (compensoPertinente ? 1 : 0) + (mostraRicavo ? 2 : 0) + (soloLettura ? 0 : 1);
  const totSpese = spese.reduce((s, x) => s + Number(x.importo), 0);
  // Al cliente si riaddebita quanto previsto dall'offerta (0,65 €/km), non il
  // rimborso riconosciuto a chi ha anticipato (0,48). Al consulente il campo
  // non arriva: la cifra verso il cliente non lo riguarda.
  const alCliente = (x) => Number(x.importo_cliente ?? x.importo ?? 0);
  const totRibalt = spese.filter(x => x.ribaltabile)
    .reduce((s, x) => s + (mostraRicavo ? alCliente(x) : Number(x.importo)), 0);

  // A corpo non ci sono righe da sommare: compenso e fatturabile discendono
  // dalla percentuale dichiarata. Senza questi addendi il riepilogo in fondo
  // restava a zero anche con l'avanzamento indicato.
  const quotaCompenso = economia ? 0 : Number(sal.quota_corpo?.compenso || 0);
  const quotaRicavo = economia ? 0 : Number(sal.quota_corpo?.ricavo || 0);
  // Dopo una rettifica fanno fede gli importi registrati, non il ricalcolo
  // dalle righe: la rettifica esiste proprio perché quel calcolo era sbagliato.
  const rettificato = (sal.rettifiche || []).length > 0;
  const compensoTotale = rettificato ? Number(sal.importo_costo || 0) - totSpese : totCosto + quotaCompenso;
  const ricavoTotale = rettificato ? Number(sal.importo_fattura || 0) - totRibalt : totRicavo + quotaRicavo;

  const vuoto = attivita.length === 0 && spese.length === 0;

  return (
    <div className="space-y-5">
      {/* Testata: si salva quando si vuole, indipendentemente dalle righe.
          Serve a mettere via un rendiconto a metà e riprenderlo dopo. */}
      {!soloLettura && testata && (
        <section className="border rounded-lg p-3 bg-white">
          <div className={`grid gap-3 ${economia ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
            <div className={economia ? '' : 'sm:col-span-1'}>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Descrizione</label>
              <input className="input text-sm" placeholder="es. Avanzamento luglio-agosto 2026"
                     value={testata.descrizione}
                     onChange={e => setTestata(t => ({ ...t, descrizione: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Data del rendiconto</label>
              <input className="input text-sm" type="date" value={testata.data_sal}
                     onChange={e => setTestata(t => ({ ...t, data_sal: e.target.value }))} />
            </div>
            {!economia && (() => {
              // La cifra tonda ha senso solo per chi ha un'offerta propria: è da
              // lì che si ricava la percentuale corrispondente.
              const offerta = compensoPertinente ? sal.quota_corpo?.importo_offerta : null;
              const importo = testata.modo === 'importo' && offerta;
              const derivata = importo && Number(testata.compenso) > 0
                ? +(Number(testata.compenso) / offerta * 100).toFixed(2) : null;
              return (
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <label className="block text-xs font-medium text-gray-500">
                      {importo ? 'Compenso di questo rendiconto (€)' : 'Avanzamento di questo rendiconto (%)'}
                    </label>
                    {offerta && (
                      <div className="flex text-[11px] rounded border border-gray-300 overflow-hidden"
                           title="Dichiara in percentuale oppure indica direttamente l'importo">
                        {[['percentuale', '%'], ['importo', '€']].map(([v, l]) => (
                          <button key={v} type="button"
                                  className={`px-2 ${testata.modo === v ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                  onClick={() => setTestata(t => ({ ...t, modo: v }))}>{l}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  {importo ? (
                    <CampoImporto className="input text-sm" value={testata.compenso}
                                  onChange={v => setTestata(t => ({ ...t, compenso: v }))} />
                  ) : (
                    <input className="input text-sm" type="number" min="0" max="100" step="0.01"
                           value={testata.percentuale ?? ''}
                           onChange={e => setTestata(t => ({ ...t, percentuale: e.target.value }))} />
                  )}
                  {derivata !== null && (
                    <p className={`text-[11px] mt-0.5 ${derivata > 100 ? 'text-red-600' : 'text-gray-500'}`}>
                      = {String(derivata).replace('.', ',')}% della tua offerta da {fmt(offerta)}
                      {derivata > 100 && ' — supera l\'offerta'}
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-3 mt-2.5">
            <button className="btn-secondary btn-sm" onClick={salvaBozza} disabled={salvando}>
              {salvando ? 'Salvo…' : '💾 Salva bozza'}
            </button>
            {salvataOra && <span className="text-xs text-green-600">✓ Salvato</span>}
            <span className="text-xs text-gray-400">
              Attività e spese si salvano da sole appena le aggiungi.
            </span>
          </div>
        </section>
      )}

      {/* Stato del rendiconto e chiusura. Le righe sono già salvate: qui si
          dichiara di aver finito. */}
      {!soloLettura && (
        <div className="flex items-center justify-between gap-3 bg-gray-50 border rounded-lg px-3 py-2 flex-wrap">
          <div className="text-xs text-gray-500">
            {sal.stato === 'bozza' && (vuoto
              ? 'Bozza vuota: aggiungi almeno un\'attività o una spesa.'
              : `Bozza · ${attivita.length} attività, ${spese.length} spese — le righe sono già salvate.`)}
            {sal.stato === 'inviato' && 'Inviato: in attesa di approvazione dal responsabile della commessa.'}
            {sal.stato === 'rifiutato' && 'Rifiutato: correggi e reinvia.'}
            {sal.stato === 'approvato' && 'Approvato: non più modificabile.'}
          </div>
          {['bozza', 'rifiutato'].includes(sal.stato) && (
            <button className="btn-primary btn-sm" disabled={vuoto} onClick={inviaInApprovazione}
                    title={vuoto ? 'Aggiungi almeno una riga' : 'Chiudi il rendiconto e mandalo in approvazione'}>
              ✓ Salva e invia in approvazione
            </button>
          )}
        </div>
      )}

      {/* ── Attività ──────────────────────────────────────────────────────
          In economia è il consuntivo del lavoro previsto. A corpo il lavoro
          previsto non si rendiconta a ore: qui si documenta soltanto quello che
          a contratto non c'era, per poterlo fatturare a parte. */}
      {(economia || attivita.length > 0 || !soloLettura) && (
        <section>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            {economia ? 'Attività svolte' : 'Attività svolte (lavoro a corpo)'}
          </h3>
          {!economia && (
            <p className="text-xs text-gray-500 -mt-1 mb-2">
              A corpo il cliente paga il corrispettivo concordato, non le ore: queste righe
              restano come traccia del lavoro svolto e base del compenso, e nel consuntivo
              non compaiono. Se una è lavoro in più da far pagare, spunta
              «nel consuntivo»: diventa una voce aggiuntiva da sottoporre al cliente.
            </p>
          )}

          {/* Il lavoro fuori contratto va concordato prima di fatturarlo:
              qui si tiene traccia del giro con il cliente. */}
          {attivita.some(a => a.extra_contratto) && (
            <div className="mb-3">
              <AutorizzazioneExtra sal={sal} staff={mostraRicavo}
                                   onCambiato={async () => { await carica(); onCambiato?.(); }} />
            </div>
          )}

          {!soloLettura && voci.length > 0 && (() => {
            const voce = voci.find(v => String(v.id) === String(rigaVoce.voce_id));
            const proposta = voce ? proponiQuantita(rigaVoce.ore, voce.unita) : null;
            return (
              <div className="bg-gray-50 border rounded-lg p-3 mb-3 space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
                  <div>
                    <label className="label text-xs">Data</label>
                    <input className="input py-1 text-sm" type="date" value={rigaVoce.data}
                           onChange={e => setRigaVoce(r => ({ ...r, data: e.target.value }))} />
                  </div>
                  <div className="md:col-span-3">
                    <label className="label text-xs">Attività (dal listino della commessa)</label>
                    <select className="input py-1 text-sm" value={rigaVoce.voce_id}
                            onChange={e => setRigaVoce(r => ({ ...r, voce_id: e.target.value }))}>
                      {voci.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.descrizione}
                          {v.quantita_prevista != null && ` — previste ${qta(v.quantita_prevista)}, residue ${qta(v.residua)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    {/* Le attività a intervento si contano, non si cronometrano:
                        il campo chiede quante ne hai svolte. */}
                    <label className="label text-xs">
                      {voce?.unita === 'intervento' ? 'Quante attività' : 'Ore lavorate'}
                    </label>
                    <input className="input py-1 text-sm" type="number"
                           step={voce?.unita === 'intervento' ? '1' : '0.25'} min="0"
                           placeholder={voce?.unita === 'intervento' ? 'es. 1' : 'es. 3,5'}
                           value={rigaVoce.ore}
                           onChange={e => setRigaVoce(r => ({ ...r, ore: e.target.value }))} />
                  </div>
                  {consulentiCommessa.length > 0 && (
                    <div>
                      <label className="label text-xs">Svolta da</label>
                      <select className="input py-1 text-sm" value={rigaVoce.consulente_id}
                              onChange={e => setRigaVoce(r => ({ ...r, consulente_id: e.target.value }))}>
                        <option value="">— chi compila —</option>
                        {consulentiCommessa.map(c => (
                          <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <button className="btn-primary btn-sm w-full" onClick={aggiungiAttivitaVoce} disabled={salvando}>+ Aggiungi</button>
                  </div>
                </div>
                {/* Il consulente vede subito come verranno conteggiate le sue ore */}
                {proposta !== null && voce && voce.unita === 'intervento' && (
                  <div className="text-xs text-gray-500">
                    <strong>{etichettaQuantita(proposta, voce.unita)}</strong> al prezzo della singola attività
                    {compensoPertinente && voce.tariffa_consulente != null && (
                      <span> · compenso {fmt(Number(voce.tariffa_consulente) * proposta)}</span>
                    )}
                    {!compensoPertinente && voce.tariffa_cliente != null && (
                      <span> · fatturabile {fmt(Number(voce.tariffa_cliente) * proposta)}</span>
                    )}
                  </div>
                )}
                {proposta !== null && voce && voce.unita !== 'ora' && voce.unita !== 'intervento' && (
                  <div className="text-xs text-gray-500">
                    {String(rigaVoce.ore).replace('.', ',')} ore → conteggiate come <strong>{etichettaQuantita(proposta, voce.unita)}</strong>
                    {compensoPertinente && voce.tariffa_consulente != null && (
                      <span> · compenso {fmt(
                        voce.unita === 'giorno' ? Number(voce.tariffa_consulente) * Number(rigaVoce.ore) / 8
                        : Number(voce.tariffa_consulente) * Number(rigaVoce.ore) / 4
                      )} (pro-rata sulle ore effettive)</span>
                    )}
                    {!compensoPertinente && voce.tariffa_cliente != null && (
                      <span> · fatturabile {fmt(Number(voce.tariffa_cliente) * proposta)}</span>
                    )}
                  </div>
                )}
                {/* Valori previsti in offerta: rendicontare oltre il previsto
                    resta possibile, ma non deve succedere senza accorgersene. */}
                {voce && voce.quantita_prevista != null && (
                  <div className={`text-xs ${Number(voce.residua) < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                    Da offerta: <strong>{qta(voce.quantita_prevista)}</strong> {ETICHETTA_UNITA[voce.unita] || ''} previste ·
                    già rendicontate {qta(voce.gia_rendicontata)} ·
                    {Number(voce.residua) < 0
                      ? <strong> sforo di {qta(-voce.residua)}</strong>
                      : <> residue <strong>{qta(voce.residua)}</strong></>}
                    {voce.da_offerta && <span className="text-gray-400"> · listino ereditato dall'offerta</span>}
                  </div>
                )}
                {/* Tariffa di riga: resta quella prevista finché non la si
                    cambia, per i progetti con condizioni diverse dal solito. */}
                {compensoPertinente && voce && (() => {
                  const prevista = voce.tariffa_consulente == null ? null : Number(voce.tariffa_consulente);
                  const variata = rigaVoce.tariffa !== '' && Number(rigaVoce.tariffa) !== prevista;
                  const perUnita = voce.unita === 'giorno' ? 'giornata'
                    : voce.unita === 'mezza_giornata' ? 'mezza giornata'
                    : voce.unita === 'intervento' ? 'attività' : 'ora';
                  return (
                    <div className="flex gap-3 items-end flex-wrap">
                      <div className="w-44">
                        <label className="label text-xs">La tua tariffa (a {perUnita})</label>
                        <CampoImporto className="input py-1 text-sm" value={rigaVoce.tariffa}
                                      placeholder={prevista !== null ? String(prevista) : ''}
                                      onChange={v => setRigaVoce(r => ({ ...r, tariffa: v }))} />
                      </div>
                      <div className="text-xs text-gray-500 pb-1.5">
                        {prevista === null
                          ? 'nessuna tariffa concordata'
                          : <>prevista {fmt(prevista)}{variata && <strong className="text-amber-700"> · variata</strong>}</>}
                      </div>
                      {variata && (
                        <div className="flex-1 min-w-[200px]">
                          <label className="label text-xs">Motivo della variazione</label>
                          <input className="input py-1 text-sm" placeholder="es. condizioni concordate per questo progetto"
                                 value={rigaVoce.tariffa_motivo}
                                 onChange={e => setRigaVoce(r => ({ ...r, tariffa_motivo: e.target.value }))} />
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* A corpo la domanda non si pone: tutto ciò che si rendiconta
                    qui è lavoro in più, e l'intestazione della sezione lo dice. */}
                {economia ? (
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" className="w-4 h-4" checked={!!rigaVoce.extra_contratto}
                           onChange={e => setRigaVoce(r => ({ ...r, extra_contratto: e.target.checked }))} />
                    Attività <strong>non prevista a contratto</strong> — non consuma le giornate
                    previste in offerta e va fatturata a parte
                  </label>
                ) : (
                  <p className="text-xs text-gray-500">
                    Tariffe concordate in offerta per gli interventi aggiuntivi: la riga si valorizza
                    da sola e va fatturata in più rispetto al prezzo a corpo, dopo il benestare del cliente.
                  </p>
                )}
                <input className="input py-1 text-sm" placeholder="Nota (facoltativa)"
                       value={rigaVoce.descrizione} onChange={e => setRigaVoce(r => ({ ...r, descrizione: e.target.value }))} />
              </div>
            );
          })()}

          {!soloLettura && voci.length === 0 && (
            <div className="bg-gray-50 border rounded-lg p-3 mb-3">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                <div>
                  <label className="label text-xs">Data</label>
                  <input className="input py-1 text-sm" type="date" value={att.data}
                         onChange={e => setAtt(a => ({ ...a, data: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <label className="label text-xs">Tipo attività</label>
                  <select className="input py-1 text-sm" value={att.tipo}
                          onChange={e => setAtt(a => ({ ...a, tipo: e.target.value }))}>
                    {TIPI.map(t => <option key={t.v} value={t.v}>{t.l} — {t.hint}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label text-xs">Unità</label>
                  <select className="input py-1 text-sm" value={att.unita}
                          onChange={e => setAtt(a => ({ ...a, unita: e.target.value, quantita: e.target.value === 'ora' ? '' : '1' }))}>
                    {UNITA.map(u => <option key={u.v} value={u.v}>{u.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label text-xs">{att.unita === 'ora' ? 'Ore' : 'Quantità'}</label>
                  <input className="input py-1 text-sm" type="number" step="0.5" min="0" value={att.quantita}
                         onChange={e => setAtt(a => ({ ...a, quantita: e.target.value }))} />
                </div>
                <div>
                  <button className="btn-primary btn-sm w-full" onClick={aggiungiAttivita} disabled={salvando}>+ Aggiungi</button>
                </div>
              </div>
              {/* Tariffa di riga, come sopra: prevista quella concordata,
                  correggibile per i progetti con condizioni diverse. */}
              {compensoPertinente && (() => {
                const mie = commessa?.mie_tariffe || sal.mie_tariffe;
                const p = mie ? mie[`${att.tipo}_${att.unita}`] : null;
                const prevista = p === null || p === undefined ? null : Number(p);
                const variata = att.tariffa !== '' && Number(att.tariffa) !== prevista;
                return (
                  <div className="flex gap-3 items-end flex-wrap mt-2">
                    <div className="w-44">
                      <label className="label text-xs">La tua tariffa</label>
                      <CampoImporto className="input py-1 text-sm" value={att.tariffa}
                                    placeholder={prevista !== null ? String(prevista) : ''}
                                    onChange={v => setAtt(a => ({ ...a, tariffa: v }))} />
                    </div>
                    <div className="text-xs text-gray-500 pb-1.5">
                      {prevista === null
                        ? 'nessuna tariffa concordata'
                        : <>prevista {fmt(prevista)}{variata && <strong className="text-amber-700"> · variata</strong>}</>}
                    </div>
                    {variata && (
                      <div className="flex-1 min-w-[200px]">
                        <label className="label text-xs">Motivo della variazione</label>
                        <input className="input py-1 text-sm" placeholder="es. condizioni concordate per questo progetto"
                               value={att.tariffa_motivo}
                               onChange={e => setAtt(a => ({ ...a, tariffa_motivo: e.target.value }))} />
                      </div>
                    )}
                  </div>
                );
              })()}
              {mostraRicavo && (
                <div className="flex gap-3 items-end flex-wrap mt-2">
                  <div className="w-44">
                    <label className="label text-xs">Fatturabile al cliente (€)</label>
                    <CampoImporto className="input py-1 text-sm" value={att.ricavo ?? ''}
                                  onChange={v => setAtt(a => ({ ...a, ricavo: v }))} />
                  </div>
                  <div className="text-xs text-gray-500 pb-1.5">
                    Da indicare quando la commessa non ha tariffe cliente da applicare.
                  </div>
                </div>
              )}
              {/* A corpo la spunta non serve: qui si registra solo lavoro
                  fuori contratto, e lo dice il titolo della sezione. */}
              {economia && (
                <label className="flex items-center gap-2 text-xs text-gray-600 mt-2">
                  <input type="checkbox" className="w-4 h-4" checked={!!att.extra_contratto}
                         onChange={e => setAtt(a => ({ ...a, extra_contratto: e.target.checked }))} />
                  Attività <strong>non prevista a contratto</strong> — da fatturare a parte
                </label>
              )}
              <input className="input py-1 text-sm mt-2" placeholder="Descrizione attività (facoltativa)"
                     value={att.descrizione} onChange={e => setAtt(a => ({ ...a, descrizione: e.target.value }))} />
            </div>
          )}

          {/* ── Accorpamenti a corpo ──────────────────────────────────────
              Gruppi di attività fatturati a un importo concordato; le altre
              restano a timesheet. Solo per noi: è un prezzo verso il cliente. */}
          {mostraRicavo && (accorpamenti.length > 0 || selezione.length > 0) && (
            <div className="mb-3 space-y-2">
              {accorpamenti.map(g => {
                const comprese = attivita.filter(a => a.accorpamento_id === g.id);
                const valore = comprese.reduce((s, a) => s + Number(a.ricavo || 0), 0);
                return (
                  <div key={g.id} className="border border-teal-200 bg-teal-50/60 rounded-lg p-2.5 text-sm">
                    {modCorpo?.id === g.id ? (
                      <div className="flex gap-2 items-end flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <label className="label text-xs">Nome (compare nel consuntivo)</label>
                          <input className="input py-1 text-sm" value={modCorpo.descrizione}
                                 onChange={e => setModCorpo(m => ({ ...m, descrizione: e.target.value }))} />
                        </div>
                        <div className="w-36">
                          <label className="label text-xs">Importo a corpo</label>
                          <CampoImporto className="input py-1 text-sm" value={modCorpo.importo}
                                        onChange={v => setModCorpo(m => ({ ...m, importo: v }))} />
                        </div>
                        <button className="btn-primary btn-sm" onClick={salvaCorpo} disabled={salvando}>✓ Salva</button>
                        <button className="btn-secondary btn-sm" onClick={() => setModCorpo(null)}>Annulla</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-600 text-white font-semibold">A CORPO</span>
                        <strong className="text-gray-800">{g.descrizione}</strong>
                        <span className="text-teal-800 font-semibold">{fmt(g.importo)}</span>
                        <span className="text-xs text-gray-500">
                          {comprese.length} attività · a timesheet varrebbero {fmt(valore)}
                        </span>
                        {puoAccorpare && (
                          <span className="ml-auto flex gap-1 flex-wrap">
                            {selezione.length > 0 && (
                              <button className="btn-sm btn bg-white border border-teal-300 text-teal-800 hover:bg-teal-50"
                                      onClick={() => aggiungiAlCorpo(g)}>
                                + aggiungi le {selezione.length} selezionate
                              </button>
                            )}
                            <button className="btn-secondary btn-sm px-2" title="Modifica nome e importo"
                                    onClick={() => setModCorpo({ id: g.id, descrizione: g.descrizione, importo: g.importo })}>✎</button>
                            <button className="btn-secondary btn-sm" onClick={() => sciogliCorpo(g)}>Sciogli</button>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {puoAccorpare && selezione.length > 0 && (() => {
                const scelte = attivita.filter(a => selezione.includes(a.id));
                const valore = scelte.reduce((s, a) => s + Number(a.ricavo || 0), 0);
                return (
                  <div className="border border-dashed border-teal-300 rounded-lg p-2.5 bg-white">
                    <div className="text-xs text-gray-600 mb-1.5">
                      <strong>{scelte.length}</strong> attività selezionate · a timesheet {fmt(valore)}.
                      Accorpale in un importo concordato con il cliente:
                    </div>
                    <div className="flex gap-2 items-end flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <label className="label text-xs">Nome (compare nel consuntivo)</label>
                        <input className="input py-1 text-sm" placeholder="es. Gestione documentale — a corpo"
                               value={nuovoCorpo.descrizione}
                               onChange={e => setNuovoCorpo(c => ({ ...c, descrizione: e.target.value }))} />
                      </div>
                      <div className="w-36">
                        <label className="label text-xs">Importo a corpo</label>
                        <CampoImporto className="input py-1 text-sm" value={nuovoCorpo.importo}
                                      placeholder={String(valore).replace('.', ',')}
                                      onChange={v => setNuovoCorpo(c => ({ ...c, importo: v }))} />
                      </div>
                      <button className="btn-primary btn-sm" onClick={creaCorpo} disabled={salvando}>
                        Accorpa a corpo
                      </button>
                      <button className="btn-secondary btn-sm" onClick={() => setSelezione([])}>Annulla selezione</button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {/* Senza un indizio la funzione non si scopre: la spunta è piccola. */}
          {puoAccorpare && accorpamenti.length === 0 && selezione.length === 0 && attivita.some(accorpabile) && (
            <p className="text-xs text-gray-400 mb-2">
              Per fatturare alcune attività a un importo a corpo, spuntale accanto alla data.
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="pb-2">Data</th><th className="pb-2">Consulente</th>
                  <th className="pb-2">Tipo</th><th className="pb-2">Unità</th>
                  <th className="pb-2 text-right">Ore</th>
                  <th className="pb-2 text-right">Fatturabile</th><th className="pb-2">Descrizione</th>
                  {compensoPertinente && <th className="pb-2 text-right">Compenso</th>}
                  {mostraRicavo && <th className="pb-2">Al cliente</th>}
                  {mostraRicavo && <th className="pb-2 text-right">{compensoPertinente ? 'Ricavo' : 'Fatturabile'}</th>}
                  {!soloLettura && <th className="pb-2"></th>}
                </tr>
              </thead>
              <tbody>
                {attivita.length === 0 && (
                  <tr><td colSpan={nColonneAtt} className="py-4 text-center text-gray-400 text-sm">Nessuna attività registrata</td></tr>
                )}
                {attivita.map(a => (
                  modifica?.kind === 'att' && modifica.id === a.id ? (
                  <tr key={a.id} className="border-b last:border-0 bg-brand-50/50">
                    <td colSpan={nColonneAtt} className="py-2.5">
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                        <div>
                          <label className="label text-xs">Data</label>
                          <input className="input py-1 text-sm" type="date" value={modifica.campi.data}
                                 onChange={e => campo('data', e.target.value)} />
                        </div>
                        {/* La voce di listino si può attaccare anche a una riga
                            che non ne aveva: è il modo di rapportare al prezzo
                            dell'offerta delle ore rimaste senza importo. */}
                        {voci.length > 0 && !modifica.campi.voce_id && (
                          <div className="md:col-span-3">
                            <label className="label text-xs">Attività (dal listino)</label>
                            <select className="input py-1 text-sm" value=""
                                    onChange={e => {
                                      if (!e.target.value) return;
                                      // Le ore sono la base: con l'unità "ore" stanno
                                      // nella quantità, altrimenti nel campo ore.
                                      const ore = modifica.campi.ore !== '' ? modifica.campi.ore
                                        : modifica.campi.unita === 'ora' ? modifica.campi.quantita : '';
                                      setModifica(m => ({ ...m, campi: { ...m.campi, voce_id: e.target.value, ore } }));
                                    }}>
                              <option value="">— fuori listino: tipo, unità e quantità —</option>
                              {voci.map(v => <option key={v.id} value={v.id}>{v.descrizione}</option>)}
                            </select>
                          </div>
                        )}
                        {modifica.campi.voce_id ? (
                          <>
                            <div className="md:col-span-3">
                              <label className="label text-xs">Attività (dal listino)</label>
                              <select className="input py-1 text-sm" value={modifica.campi.voce_id}
                                      onChange={e => campo('voce_id', e.target.value)}>
                                {voci.map(v => <option key={v.id} value={v.id}>{v.descrizione}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="label text-xs">Ore lavorate</label>
                              <input className="input py-1 text-sm" type="number" step="0.25" min="0"
                                     value={modifica.campi.ore} onChange={e => campo('ore', e.target.value)} />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="md:col-span-2">
                              <label className="label text-xs">Tipo attività</label>
                              <select className="input py-1 text-sm" value={modifica.campi.tipo}
                                      onChange={e => campo('tipo', e.target.value)}>
                                {TIPI.map(t => <option key={t.v} value={t.v}>{t.l} — {t.hint}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="label text-xs">Unità</label>
                              <select className="input py-1 text-sm" value={modifica.campi.unita}
                                      onChange={e => campo('unita', e.target.value)}>
                                {UNITA.map(u => <option key={u.v} value={u.v}>{u.l}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="label text-xs">Quantità</label>
                              <input className="input py-1 text-sm" type="number" step="0.5" min="0"
                                     value={modifica.campi.quantita} onChange={e => campo('quantita', e.target.value)} />
                            </div>
                          </>
                        )}
                        {consulentiCommessa.length > 0 && (
                          <div>
                            <label className="label text-xs">Svolta da</label>
                            <select className="input py-1 text-sm" value={modifica.campi.consulente_id}
                                    onChange={e => campo('consulente_id', e.target.value)}>
                              <option value="">— chi compila —</option>
                              {consulentiCommessa.map(c => (
                                <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      {compensoPertinente && (
                        <div className="flex gap-3 items-end flex-wrap mt-2">
                          <div className="w-40">
                            <label className="label text-xs">La tua tariffa</label>
                            <CampoImporto className="input py-1 text-sm" value={modifica.campi.tariffa}
                                          placeholder={a.tariffa_prevista != null ? String(Number(a.tariffa_prevista)) : ''}
                                          onChange={v => campo('tariffa', v)} />
                          </div>
                          <div className="text-xs text-gray-500 pb-1.5">
                            {a.tariffa_prevista != null
                              ? <>prevista {fmt(a.tariffa_prevista)} — vuoto per applicarla</>
                              : 'nessuna tariffa concordata'}
                          </div>
                          {modifica.campi.tariffa !== '' && Number(modifica.campi.tariffa) !== Number(a.tariffa_prevista) && (
                            <div className="flex-1 min-w-[200px]">
                              <label className="label text-xs">Motivo della variazione</label>
                              <input className="input py-1 text-sm" value={modifica.campi.tariffa_motivo}
                                     placeholder="es. condizioni concordate per questo progetto"
                                     onChange={e => campo('tariffa_motivo', e.target.value)} />
                            </div>
                          )}
                        </div>
                      )}
                      {/* L'importo verso il cliente: su una commessa a corpo non
                          ci sono tariffe cliente e senza questo campo l'attività
                          aggiuntiva resterebbe senza valore. */}
                      {mostraRicavo && (
                        <div className="flex gap-3 items-end flex-wrap mt-2">
                          <div className="w-44">
                            <label className="label text-xs">Fatturabile al cliente (€)</label>
                            <CampoImporto className="input py-1 text-sm" value={modifica.campi.ricavo}
                                          onChange={v => campo('ricavo', v)} />
                          </div>
                          <div className="text-xs text-gray-500 pb-1.5">
                            Lascialo vuoto per nessun importo.
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2 mt-2 items-center flex-wrap">
                        <input className="input py-1 text-sm flex-1 min-w-[160px]" placeholder="Descrizione (facoltativa)"
                               value={modifica.campi.descrizione}
                               onChange={e => campo('descrizione', e.target.value)} />
                        {economia && (
                          <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                            <input type="checkbox" className="w-4 h-4" checked={!!modifica.campi.extra_contratto}
                                   onChange={e => campo('extra_contratto', e.target.checked)} />
                            fuori contratto
                          </label>
                        )}
                        {/* Ore oltre il budget concordato: restano scritte, ma
                            non le paga né il cliente né noi. */}
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap"
                               title="Ore svolte oltre il budget concordato: documentate, ma senza compenso né addebito">
                          <input type="checkbox" className="w-4 h-4" checked={!!modifica.campi.fuori_budget}
                                 onChange={e => campo('fuori_budget', e.target.checked)} />
                          fuori budget
                        </label>
                        <button className="btn-primary btn-sm" onClick={salvaModifica} disabled={salvando}>
                          {salvando ? 'Salvo…' : '✓ Salva'}
                        </button>
                        <button className="btn-secondary btn-sm" onClick={() => setModifica(null)}>Annulla</button>
                      </div>
                    </td>
                  </tr>
                  ) : (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="py-2 text-gray-600 whitespace-nowrap">
                      {accorpabile(a) && (
                        <input type="checkbox" className="w-4 h-4 mr-1.5 align-middle"
                               checked={selezione.includes(a.id)} onChange={() => seleziona(a.id)}
                               title="Seleziona per accorparla in un importo a corpo" />
                      )}
                      {fmtDate(a.data)}
                    </td>
                    <td className="py-2 text-gray-700">{a.consulente_nome || sal.consulente_nome || sal.utente_nome || '—'}</td>
                    <td className="py-2">{etichettaTipo(a.tipo)}</td>
                    <td className="py-2 text-gray-500">{etichettaUnita(a.unita)}</td>
                    <td className="py-2 text-right text-gray-600">
                      {/* Con l'unità "ore" le ore SONO la quantità: leggendo solo
                          il campo ore la colonna restava vuota mentre il totale
                          in fondo le contava. */}
                      {(() => {
                        const ore = a.ore != null ? Number(a.ore)
                          : a.unita === 'ora' ? Number(a.quantita) : null;
                        return ore === null ? '—' : String(ore).replace('.', ',');
                      })()}
                    </td>
                    <td className="py-2 text-right font-medium">
                      {/* L'amministratore può correggere la quantità fatturabile:
                          l'arrotondamento è una proposta, l'ultima parola è sua. */}
                      {/* L'unità va scritta accanto al campo: un "5" in una
                          casella muta, su una voce a giornata, vale cinque
                          giornate — 2.500 € per cinque ore di lavoro. */}
                      {mostraRicavo && a.ore != null ? (
                        <span className="whitespace-nowrap">
                          <input
                            className="input py-0.5 text-sm text-right w-16 inline-block"
                            type="number" step="0.25" min="0" defaultValue={Number(a.quantita)}
                            title={`Quantità fatturabile in ${ETICHETTA_UNITA[a.unita] || a.unita}, `
                              + `non in ore: ${String(Number(a.ore)).replace('.', ',')} ore di lavoro `
                              + `valgono ${String(proponiQuantita(a.ore, a.unita)).replace('.', ',')}`}
                            onBlur={e => {
                              const v = Number(e.target.value);
                              if (v !== Number(a.quantita) && v >= 0) correggiQuantita(a.id, v);
                            }}
                          />
                          <span className="text-[11px] text-gray-400 ml-1">
                            {ETICHETTA_UNITA[a.unita] || a.unita}
                          </span>
                        </span>
                      ) : Number(a.quantita)}
                    </td>
                    <td className="py-2 text-gray-600 max-w-[220px]">
                      <div className="truncate">{a.descrizione || '—'}</div>
                      {/* A corpo lo dice già il titolo della sezione: qui la
                          targhetta serve solo dove le due cose convivono. */}
                      {economia && a.extra_contratto && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                          fuori contratto
                        </span>
                      )}
                      {a.fuori_budget && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700"
                              title="Oltre il budget concordato: nessun compenso, nessun addebito">
                          fuori budget
                        </span>
                      )}
                      {mostraRicavo && a.accorpamento_id && (() => {
                        const g = accorpamenti.find(x => x.id === a.accorpamento_id);
                        return (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 inline-flex items-center gap-1">
                            a corpo{g ? `: ${g.descrizione}` : ''}
                            {puoAccorpare && (
                              <button className="hover:text-red-600 font-bold" title="Togli dall'accorpamento"
                                      onClick={() => togliDalCorpo(a)}>×</button>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    {compensoPertinente && (() => {
                      // La variazione di tariffa si vede sulla riga: un compenso
                      // diverso dal solito senza spiegazione è ciò che fa perdere
                      // tempo a chi approva.
                      const variata = a.tariffa_applicata != null && a.tariffa_prevista != null
                        && Number(a.tariffa_applicata) !== Number(a.tariffa_prevista);
                      return (
                        <td className="py-2 text-right">
                          {a.costo === null || a.costo === undefined
                            ? <span className="text-amber-600 text-xs" title="Tariffa non concordata">tariffa mancante</span>
                            : fmt(a.costo)}
                          {variata && (
                            <div className="text-[11px] text-amber-700"
                                 title={a.tariffa_motivo || 'Tariffa diversa da quella concordata'}>
                              tariffa {fmt(a.tariffa_applicata)} anziché {fmt(a.tariffa_prevista)}
                            </div>
                          )}
                        </td>
                      );
                    })()}
                    {mostraRicavo && (
                      <td className="py-2 text-xs">
                        {/* A corpo il cliente paga il corrispettivo concordato:
                            le ore sono la nostra traccia del lavoro svolto e la
                            base del compenso, e nel consuntivo non ci vanno.
                            Prima l'unico modo per togliere le ore dal documento
                            era cancellarle, perdendo anche il compenso. */}
                        {soloLettura ? (
                          <span className={a.al_cliente === false ? 'text-gray-400' : 'text-gray-600'}>
                            {a.al_cliente === false ? 'traccia interna' : 'nel consuntivo'}
                          </span>
                        ) : (
                          <label className="flex items-center gap-1.5 cursor-pointer"
                                 title={a.al_cliente === false
                                   ? 'Resta fuori dal consuntivo al cliente: spunta per sottoporgliela come lavoro in più'
                                   : 'Compare nel consuntivo al cliente'}>
                            <input type="checkbox" className="w-4 h-4" checked={a.al_cliente !== false}
                                   onChange={e => cambiaAlCliente(a.id, e.target.checked)} />
                            <span className={a.al_cliente === false ? 'text-gray-400' : 'text-gray-700 font-medium'}>
                              {a.al_cliente === false ? 'traccia interna' : 'nel consuntivo'}
                            </span>
                          </label>
                        )}
                      </td>
                    )}
                    {mostraRicavo && (
                      <td className="py-2 text-right text-brand-600">
                        {/* Accorpata: il suo valore a tariffa resta leggibile, ma
                            barrato — si fattura col gruppo, non da sola. */}
                        {a.accorpamento_id
                          ? <span className="text-gray-400 line-through" title="Fatturata con l'accorpamento a corpo">
                              {a.ricavo === null ? '—' : fmt(a.ricavo)}
                            </span>
                          : (a.ricavo === null ? '—' : fmt(a.ricavo))}
                      </td>
                    )}
                    {!soloLettura && (
                      <td className="py-2 text-right whitespace-nowrap">
                        <button className="btn-secondary btn-sm px-2" title="Correggi la riga"
                                onClick={() => apriAtt(a)}>✎</button>
                        <button className="btn-danger btn-sm ml-1" title="Elimina la riga"
                                onClick={() => elimina('att', a.id)}>✕</button>
                      </td>
                    )}
                  </tr>
                  )
                ))}
              </tbody>
              {attivita.length > 0 && (
                <tfoot>
                  <tr className="font-semibold border-t-2">
                    <td className="pt-2" colSpan={4}>
                      Totale {totGiorni > 0 && `${String(totGiorni).replace('.', ',')} gg fatturabili`}
                      {totGiorni > 0 && totOre > 0 && ' · '}{totOre > 0 && `${totOre} ore`}
                    </td>
                    <td className="pt-2 text-right text-gray-600">
                      {totOreEffettive > 0 ? String(totOreEffettive).replace('.', ',') : ''}
                    </td>
                    <td></td><td></td>
                    {compensoPertinente && <td className="pt-2 text-right">{fmt(totCosto)}</td>}
                    {mostraRicavo && (
                      <td className="pt-2 text-xs font-normal text-gray-500">
                        {nInterne > 0 && `${nInterne} solo traccia`}
                      </td>
                    )}
                    {mostraRicavo && <td className="pt-2 text-right text-brand-600">{fmt(totRicavo)}</td>}
                    {!soloLettura && <td></td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      )}

      {/* ── Rettifiche: il prima e il dopo restano leggibili ─────────────── */}
      {rettificato && (
        <section className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
          <div className="font-medium text-amber-900">Rendiconto rettificato dopo l'approvazione</div>
          <div className="text-xs text-amber-800 mt-1 space-y-1.5">
            {sal.rettifiche.map(r => (
              <div key={r.id}>
                <strong>{fmtDate(r.data)}</strong>
                {r.percentuale_dopo != null && Number(r.percentuale_prima) !== Number(r.percentuale_dopo) && (
                  <> · avanzamento {Number(r.percentuale_prima) || 0}% → {Number(r.percentuale_dopo)}%</>
                )}
                {compensoPertinente && Number(r.costo_prima) !== Number(r.costo_dopo) && (
                  <> · {mostraRicavo ? 'credito consulente' : 'tuo credito'} {fmt(r.costo_prima)} → {fmt(r.costo_dopo)}</>
                )}
                {mostraRicavo && r.fattura_prima != null && Number(r.fattura_prima) !== Number(r.fattura_dopo) && (
                  <> · fatturato {fmt(r.fattura_prima)} → {fmt(r.fattura_dopo)}</>
                )}
                <div className="text-amber-700">{r.motivo}{r.autore ? ` — ${r.autore}` : ''}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Dove è finito questo rendiconto ────────────────────────────────
          Approvato genera una fattura; quando la fattura è emessa il
          rendiconto è chiuso, e il riferimento resta qui invece di dover
          cercare in Fatturazione quale documento lo copriva. */}
      {sal.stato === 'approvato' && (
        <section className={`border rounded-lg p-3 text-sm ${
          sal.fattura_stato === 'pagata' ? 'bg-green-50 border-green-200'
          : ['emessa'].includes(sal.fattura_stato) ? 'bg-green-50/70 border-green-200'
          : 'bg-amber-50/70 border-amber-200'}`}>
          {['emessa', 'pagata'].includes(sal.fattura_stato) ? (
            <>
              Fatturato al cliente
              {sal.fattura_data && <> il <strong>{new Date(sal.fattura_data).toLocaleDateString('it-IT')}</strong></>}
              {sal.fattura_numero && <> con fattura <strong>n. {sal.fattura_numero}</strong></>}
              {mostraRicavo && sal.fattura_importo && <> · {fmt(sal.fattura_importo)}</>}
              {sal.fattura_pagamento && <> · incassata il {new Date(sal.fattura_pagamento).toLocaleDateString('it-IT')}</>}
              {!sal.fattura_numero && (
                <span className="text-xs text-gray-500"> — numero non registrato: aggiungilo spuntando la fattura nel riepilogo del mese</span>
              )}
            </>
          ) : (
            <>
              Approvato: la fattura è <strong>in coda</strong>, da emettere.
              {mostraRicavo && <> Spuntala nel riepilogo del mese quando l'avrai emessa in Fatture in Cloud.</>}
            </>
          )}
        </section>
      )}

      {/* ── Ordine del cliente ──────────────────────────────────────────────
          Dove il cliente lavora per ordini, chi prepara il rendiconto deve
          sapere quanto resta ordinato prima di proporre l'importo. Ai
          consulenti esterni il blocco non arriva: sono importi verso il
          cliente. */}
      {sal.ordini?.ha_ordini && (() => {
        const attivi = sal.ordini.ordini.filter(o => o.stato === 'attivo');
        if (!attivi.length) return null;
        return (
          <section className="bg-gray-50 border rounded-lg p-3 text-sm text-gray-700">
            <div className="text-xs text-gray-500 mb-1">Ordine del cliente</div>
            {attivi.map(o => (
              <div key={o.id} className="flex flex-wrap gap-x-3 gap-y-0.5">
                <span className="font-mono text-gray-800">{o.numero}</span>
                {o.importo ? <span>ordinati <strong>{fmt(o.importo)}</strong></span> : null}
                <span className="text-gray-500">fatturati {fmt(o.fatturato)}</span>
                {o.in_coda ? <span className="text-amber-700">in coda {fmt(o.in_coda)}</span> : null}
                {o.residuo !== null && (
                  <span className={o.residuo < -0.01 ? 'text-red-600 font-semibold' : 'text-green-700 font-semibold'}>
                    residuo {fmt(o.residuo)}
                  </span>
                )}
                {o.scaduto && <span className="text-red-600">scaduto il {new Date(o.scadenza).toLocaleDateString('it-IT')}</span>}
              </div>
            ))}
            <p className="text-xs text-gray-500 mt-1">
              La fattura che nasce da questo rendiconto citerà il numero d'ordine e non dovrebbe superarne il residuo.
            </p>
          </section>
        );
      })()}

      {/* ── Commesse a consuntivo: l'importo nasce dalle righe ──────────── */}
      {economia && mostraRicavo && (
        <section className="bg-brand-50/70 border border-brand-100 rounded-lg p-3 text-sm text-gray-700">
          Commessa <strong>a consuntivo</strong>: l'importo da fatturare è la somma delle righe
          valorizzate alle tariffe del listino, non una percentuale di avanzamento. Cambiare una
          percentuale qui non cambierebbe l'importo — per fatturare di più o di meno si correggono
          le quantità, oppure l'importo in fase di approvazione.
        </section>
      )}

      {/* ── Commesse a corpo: avanzamento percentuale ───────────────────── */}
      {!economia && (() => {
        const perc = Number(sal.percentuale) || 0;
        const budget = Number(sal.commessa_budget) || 0;   // assente per gli esterni
        // Dopo una rettifica il compenso è quello registrato, non la quota
        // ricalcolata dalla percentuale.
        const q = rettificato && sal.quota_corpo
          ? { ...sal.quota_corpo, compenso: compensoTotale }
          : sal.quota_corpo;
        return (
          <section className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-gray-700 space-y-1">
            <div>
              Commessa <strong>a corpo</strong>: conta l'avanzamento dichiarato, non le ore.
              Questo rendiconto vale <strong>{perc}%</strong> del lavoro.
            </div>

            {/* Quanto matura chi rendiconta. Prima non compariva da nessuna
                parte e il rendiconto sembrava non essere stato registrato. */}
            {compensoPertinente && perc > 0 && (
              q?.importo_offerta ? (
                <div className="text-gray-700">
                  Compenso maturato: <strong>{fmt(q.compenso)}</strong>
                  <span className="text-xs text-gray-500">
                    {' '}— {perc}% della tua offerta da {fmt(q.importo_offerta)}.
                  </span>
                </div>
              ) : (
                <div className="text-amber-700 text-xs">
                  Non risulta una tua offerta collegata a questa commessa: il compenso non
                  può essere calcolato. Segnalalo a Sinthema, il rendiconto resta valido.
                </div>
              )
            )}

            {budget > 0 && perc > 0 && (
              <div className="text-gray-600">
                Importo che verrà proposto in fattura: <strong>{fmt(budget * perc / 100)}</strong>
                <span className="text-xs text-gray-500"> — {perc}% di {fmt(budget)}. Resta correggibile in approvazione.</span>
              </div>
            )}
            {/* Il lavoro fuori contratto non entra nella percentuale: si somma. */}
            {attivita.length > 0 && (
              <div className="text-gray-700">
                In più {attivita.length} {attivita.length === 1 ? 'attività non prevista' : 'attività non previste'} a contratto
                {mostraRicavo && totRicavo > 0 && <> — <strong>{fmt(totRicavo)}</strong> da fatturare in aggiunta</>}
                {compensoPertinente && totCosto > 0 && <> · tuo compenso aggiuntivo <strong>{fmt(totCosto)}</strong></>}.
              </div>
            )}
            <div className="text-xs text-gray-500">
              Le spese qui sotto sono facoltative: se non ce ne sono, lascia la sezione vuota.
            </div>
          </section>
        );
      })()}

      {/* ── Spese ───────────────────────────────────────────────────────── */}
      <section>
        <div className="flex items-baseline gap-2 mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Spese sostenute</h3>
          <span className="text-xs text-gray-400">facoltative</span>
          {spese.length === 0 && !soloLettura && (
            <button type="button"
                    className={`ml-auto btn-sm btn ${pannelloSpese
                      ? 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                      : 'bg-brand-600 text-white hover:bg-brand-700'}`}
                    onClick={() => setMostraSpese(!pannelloSpese)}>
              {pannelloSpese ? 'Nascondi' : '+ Aggiungi una spesa'}
            </button>
          )}
        </div>

        {spese.length === 0 && !pannelloSpese && (
          <p className="text-xs text-gray-400 mb-3">
            Nessuna spesa. Se non ce ne sono puoi inviare il rendiconto così com'è.
          </p>
        )}

        {!soloLettura && (spese.length > 0 || pannelloSpese) && (
          <div className="bg-gray-50 border rounded-lg p-3 mb-3">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
              <div>
                <label className="label text-xs">Data</label>
                <input className="input py-1 text-sm" type="date" value={spesa.data}
                       onChange={e => setSpesa(s => ({ ...s, data: e.target.value }))} />
              </div>
              <div>
                <label className="label text-xs">Categoria</label>
                <select className="input py-1 text-sm" value={spesa.categoria}
                        onChange={e => setSpesa(s => ({ ...s, categoria: e.target.value, km: '', importo: '' }))}>
                  {CATEGORIE.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
              {spesa.categoria === 'km' && (
                <div>
                  <label className="label text-xs">Km percorsi</label>
                  <input className="input py-1 text-sm" type="number" step="1" min="0" value={spesa.km}
                         onChange={e => setSpesa(s => ({ ...s, km: e.target.value }))} />
                </div>
              )}
              {spesa.categoria === 'km' && (
                <div>
                  <label className="label text-xs">€/km rimborsati</label>
                  <CampoImporto className="input py-1 text-sm" value={spesa.tariffa_km}
                                placeholder={String(kmPrevista).replace('.', ',')}
                                onChange={v => setSpesa(s => ({ ...s, tariffa_km: v }))} />
                  {/* I chilometri sono due numeri: questo è quello che paghiamo
                      a chi guida. Quello che riaddebitiamo al cliente viene dal
                      contratto, ed è scritto sulla commessa. */}
                  {mostraRicavo && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Al cliente si riaddebitano {String(kmCliente).replace('.', ',')} €/km
                      {kmCliente === 0 && ' (spese comprese nel corrispettivo)'}
                      {commessa?.tariffa_km_cliente === null || commessa?.tariffa_km_cliente === undefined
                        ? ' — tariffa standard, si cambia sulla commessa' : ''}
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="label text-xs">Importo €</label>
                <input className="input py-1 text-sm" type="number" step="0.01" min="0"
                       placeholder={importoProposto !== null ? String(importoProposto) : ''}
                       value={spesa.importo}
                       onChange={e => setSpesa(s => ({ ...s, importo: e.target.value }))} />
                {spesa.categoria === 'km' && (
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    {importoProposto !== null
                      ? `proposto ${fmt(importoProposto)} (${tariffaKm} €/km) — modificabile`
                      : `tariffa ${tariffaKm} €/km`}
                  </div>
                )}
              </div>
              {consulentiCommessa.length > 0 && (
                <div>
                  <label className="label text-xs">Sostenuta da</label>
                  <select className="input py-1 text-sm" value={spesa.consulente_id}
                          onChange={e => setSpesa(x => ({ ...x, consulente_id: e.target.value }))}>
                    <option value="">— chi compila —</option>
                    {consulentiCommessa.map(c => (
                      <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="label text-xs">Giustificativo</label>
                <input className="text-xs w-full" type="file" accept="image/*,application/pdf"
                       onChange={e => setFile(e.target.files?.[0] || null)} />
              </div>
              <div>
                <button className="btn-primary btn-sm w-full" onClick={aggiungiSpesa} disabled={salvando}>+ Aggiungi</button>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2">
              <input className="input py-1 text-sm flex-1" placeholder="Descrizione (facoltativa)"
                     value={spesa.descrizione} onChange={e => setSpesa(s => ({ ...s, descrizione: e.target.value }))} />
              <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                <input type="checkbox" className="w-4 h-4" checked={spesa.ribaltabile}
                       onChange={e => setSpesa(s => ({ ...s, ribaltabile: e.target.checked }))} />
                Da riaddebitare al cliente
              </label>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="pb-2">Data</th><th className="pb-2">Consulente</th>
                <th className="pb-2">Categoria</th><th className="pb-2">Descrizione</th>
                <th className="pb-2 text-right">Importo</th>
                {mostraRicavo && <th className="pb-2 text-right">Al cliente</th>}
                <th className="pb-2">Giustificativo</th>
                <th className="pb-2">Riaddebito</th>
                {!soloLettura && <th className="pb-2"></th>}
              </tr>
            </thead>
            <tbody>
              {spese.length === 0 && (
                <tr><td colSpan={mostraRicavo ? 9 : 8} className="py-4 text-center text-gray-400 text-sm">Nessuna spesa registrata</td></tr>
              )}
              {spese.map(s => (
                modifica?.kind === 'spesa' && modifica.id === s.id ? (
                <tr key={s.id} className="border-b last:border-0 bg-brand-50/50">
                  <td colSpan={mostraRicavo ? 9 : 8} className="py-2.5">
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                      <div>
                        <label className="label text-xs">Data</label>
                        <input className="input py-1 text-sm" type="date" value={modifica.campi.data}
                               onChange={e => campo('data', e.target.value)} />
                      </div>
                      <div>
                        <label className="label text-xs">Categoria</label>
                        <select className="input py-1 text-sm" value={modifica.campi.categoria}
                                onChange={e => campo('categoria', e.target.value)}>
                          {CATEGORIE.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                        </select>
                      </div>
                      {modifica.campi.categoria === 'km' && (
                        <div>
                          <label className="label text-xs">Km percorsi</label>
                          <input className="input py-1 text-sm" type="number" step="1" min="0"
                                 value={modifica.campi.km} onChange={e => campo('km', e.target.value)} />
                        </div>
                      )}
                      {modifica.campi.categoria === 'km' && (
                        <div>
                          <label className="label text-xs">€/km</label>
                          <CampoImporto className="input py-1 text-sm" value={modifica.campi.tariffa_km}
                                        placeholder={String(kmPrevista).replace('.', ',')}
                                        onChange={v => campo('tariffa_km', v)} />
                        </div>
                      )}
                      <div>
                        <label className="label text-xs">Importo €</label>
                        <input className="input py-1 text-sm" type="number" step="0.01" min="0"
                               value={modifica.campi.importo} onChange={e => campo('importo', e.target.value)} />
                      </div>
                      {consulentiCommessa.length > 0 && (
                        <div>
                          <label className="label text-xs">Sostenuta da</label>
                          <select className="input py-1 text-sm" value={modifica.campi.consulente_id}
                                  onChange={e => campo('consulente_id', e.target.value)}>
                            <option value="">— chi compila —</option>
                            {consulentiCommessa.map(c => (
                              <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="label text-xs">
                          Giustificativo {s.ha_allegato && <span className="text-gray-400">(già presente)</span>}
                        </label>
                        <input className="text-xs w-full" type="file" accept="image/*,application/pdf"
                               onChange={e => setFileModifica(e.target.files?.[0] || null)} />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2 items-center flex-wrap">
                      <input className="input py-1 text-sm flex-1 min-w-[160px]" placeholder="Descrizione (facoltativa)"
                             value={modifica.campi.descrizione}
                             onChange={e => campo('descrizione', e.target.value)} />
                      <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                        <input type="checkbox" className="w-4 h-4" checked={!!modifica.campi.ribaltabile}
                               onChange={e => campo('ribaltabile', e.target.checked)} />
                        Da riaddebitare al cliente
                      </label>
                      <button className="btn-primary btn-sm" onClick={salvaModifica} disabled={salvando}>
                        {salvando ? 'Salvo…' : '✓ Salva'}
                      </button>
                      <button className="btn-secondary btn-sm" onClick={() => setModifica(null)}>Annulla</button>
                    </div>
                  </td>
                </tr>
                ) : (
                <tr key={s.id} className="border-b last:border-0">
                  <td className="py-2 text-gray-600">{fmtDate(s.data)}</td>
                  <td className="py-2 text-gray-700">{s.consulente_nome || sal.consulente_nome || sal.utente_nome || '—'}</td>
                  <td className="py-2">
                    {etichettaCat(s.categoria)}
                    {s.categoria === 'km' && s.km && <span className="text-xs text-gray-400 ml-1">{Number(s.km)} km</span>}
                  </td>
                  <td className="py-2 text-gray-600 max-w-[200px] truncate">{s.descrizione || '—'}</td>
                  <td className="py-2 text-right font-medium">{fmt(s.importo)}</td>
                  {/* Quanto si riaddebita: la tariffa dell'offerta, non la
                      nostra. Se differisce dal rimborso lo si vede qui. */}
                  {mostraRicavo && (
                    <td className="py-2 text-right">
                      {s.ribaltabile ? (
                        <span className={alCliente(s) !== Number(s.importo) ? 'text-brand-700 font-medium' : ''}>
                          {fmt(alCliente(s))}
                          {s.tariffa_km_cliente && (
                            <span className="block text-[11px] font-normal text-gray-400">
                              {s.tariffa_km_cliente} €/km
                            </span>
                          )}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                  )}
                  <td className="py-2">
                    {s.ha_allegato
                      ? <button className="text-brand-600 hover:underline text-xs"
                                onClick={() => apriProtetto(`/sal/${salId}/spese/${s.id}/allegato`, s.allegato_nome)
                                  .catch(e => alert(e))}>📎 apri</button>
                      : <span className="text-amber-600 text-xs">mancante</span>}
                  </td>
                  <td className="py-2 text-xs">
                    {mostraRicavo ? (
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" className="w-4 h-4" checked={!!s.ribaltata}
                               onChange={e => cambiaRibalto(s.id, e.target.checked)} />
                        <span className={s.ribaltata ? 'text-green-700 font-medium' : 'text-gray-500'}>
                          {s.ribaltata ? 'riaddebitata' : 'non riaddebitata'}
                        </span>
                      </label>
                    ) : (
                      <span className={s.ribaltabile ? 'text-gray-600' : 'text-gray-400'}>
                        {s.ribaltabile ? 'proposta al cliente' : 'a mio carico'}
                      </span>
                    )}
                  </td>
                  {!soloLettura && (
                    <td className="py-2 text-right whitespace-nowrap">
                      <button className="btn-secondary btn-sm px-2" title="Correggi la spesa"
                              onClick={() => apriSpesa(s)}>✎</button>
                      <button className="btn-danger btn-sm ml-1" title="Elimina la spesa"
                              onClick={() => elimina('spesa', s.id)}>✕</button>
                    </td>
                  )}
                </tr>
                )
              ))}
            </tbody>
            {spese.length > 0 && (
              <tfoot>
                <tr className="font-semibold border-t-2">
                  <td className="pt-2" colSpan={4}>Totale spese</td>
                  <td className="pt-2 text-right">{fmt(totSpese)}</td>
                  {mostraRicavo && <td className="pt-2 text-right text-brand-700">{fmt(totRibalt)}</td>}
                  <td></td>
                  <td className="pt-2 text-xs font-normal text-gray-500">
                    {mostraRicavo ? 'riaddebito secondo offerta' : `di cui ${fmt(totRibalt)} al cliente`}
                  </td>
                  {!soloLettura && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      {/* ── Riepilogo economico ─────────────────────────────────────────── */}
      {/* Per un rendiconto interno il costo non si misura qui: l'unico numero
          che serve è quanto fatturare al cliente. Mostrare compenso e margine
          darebbe un margine pari al ricavo, cioè un dato falso. */}
      <section className={`grid grid-cols-2 gap-3 ${compensoPertinente ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        {compensoPertinente && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">
              {economia ? 'Compenso attività' : 'Compenso avanzamento'}
            </div>
            <div className="text-lg font-bold text-gray-800 mt-0.5">{fmt(compensoTotale)}</div>
          </div>
        )}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">Spese sostenute</div>
          <div className="text-lg font-bold text-gray-800 mt-0.5">{fmt(totSpese)}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500">
            {!compensoPertinente ? 'di cui riaddebitate' : mostraRicavo ? 'Totale a costo' : 'Totale a mio credito'}
          </div>
          <div className="text-lg font-bold text-green-700 mt-0.5">
            {fmt(compensoPertinente ? compensoTotale + totSpese : totRibalt)}
          </div>
        </div>
        {mostraRicavo && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">Fatturabile al cliente</div>
            <div className="text-lg font-bold text-brand-600 mt-0.5">{fmt(ricavoTotale + totRibalt)}</div>
            {compensoPertinente && (
              <div className="text-[11px] text-gray-400 mt-0.5">
                margine {fmt((ricavoTotale + totRibalt) - (compensoTotale + totSpese))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
